import React from 'react';
import { useFormContext } from 'react-hook-form';
import ActionCard from './ActionCard';
import { EqualisationFormValues } from './FormWrapper';
import { CurrencyCode } from 'further-types/lp/vehicle';
import EqualisationFooter from '../EqualisationFooter';
import { useParams } from 'react-router-dom';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useEqualisationSection } from '../EqualisationContext';
import api from 'lib/trpcClient';
import { EqualisationSection } from 'further-types/lp/equalisation';
import allocationsAreValid from './helpers/allocationsAreValid';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import EqualisationPage from '../EqualisationPage';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { numberToCurrencyString, pluralize } from 'further-ui/utils';
import AvatarWithName from 'components/new/AvatarWithName';
import { AllocationRule } from 'further-types/lp/allocations';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import { amber, grey } from '@mui/material/colors';
import { match } from 'ts-pattern';

interface EqualisationContentProps {
  baseCurrency: CurrencyCode;
}

const EqualisationContent: React.FC<EqualisationContentProps> = ({
  baseCurrency,
}) => {
  const { getValues, watch } = useFormContext<EqualisationFormValues>();
  const actions = (watch('actions') ?? []).map((action) => ({
    id: action.actionId,
    ...action,
  }));
  const {
    previousSection,
    nextSection,
    currentSection,
    goToNextSection,
    goToPreviousSection,
    goToListPage,
  } = useEqualisationSection();
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();

  const { expandedRows, toggleRowExpansion } = useExpandedRows(
    actions
      .filter((action) => action.allocationRule === AllocationRule.Manual)
      .map((action) => action.id),
  );

  const updateEqualisation = api.equalisations.update.useMutation(
    useHandleTrpcMutation()({}),
  );

  const allocationBreakdownIsValid = watch('actions').every((action) =>
    allocationsAreValid(action),
  );

  const handleNext = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: nextSection ?? EqualisationSection.Summary,
        actions: getValues('actions'),
      },
      {
        onSuccess: () => {
          goToNextSection();
        },
      },
    );
  };

  const handleSaveAsDraft = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection,
        actions: getValues('actions'),
      },
      {
        onSuccess: () => {
          goToListPage();
        },
      },
    );
  };

  const handlePrevious = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: previousSection ?? EqualisationSection.Commitments,
        actions: getValues('actions'),
      },
      {
        onSuccess: () => {
          goToPreviousSection();
        },
      },
    );
  };

  const columns: Array<TableColumn<(typeof actions)[number]>> = [
    {
      key: 'counterpartyName',
      label: 'Counterparty',
      render: (action) => {
        const excludedInvestors = action.newAllocations.filter(
          (allocation) => allocation.excludeFromAllocation,
        );
        const excludingInvestorsCopy =
          excludedInvestors.length > 0
            ? `excluding ${excludedInvestors.length} ${pluralize(
                'investor',
                excludedInvestors.length,
              )}`
            : '';
        return (
          <AvatarWithName
            avatarSize="lg"
            name={action.counterpartyLabel}
            subtext={
              action.allocationRule === AllocationRule.Manual
                ? 'Manually allocated'
                : [
                    `Allocated by commitment and close date`,
                    excludingInvestorsCopy,
                  ]
                    .filter(Boolean)
                    .join(' ')
            }
          />
        );
      },
    },
    {
      key: 'actionType',
      label: 'Type',
      render: (action) =>
        action.linkedActions.length > 0
          ? action.actionType +
            ` and ${action.linkedActions.length} ${pluralize(
              'linked action',
              action.linkedActions.length,
            )}`
          : action.actionType,
    },
    {
      key: 'date',
      label: 'Date',
      render: (action) => action.ledgerDate.toReadable(),
    },
    {
      key: 'amountLcy',
      label: 'Amount',
      render: (action) =>
        numberToCurrencyString(action.totalAmountLcy, {
          currency: baseCurrency,
        }),
    },
  ];

  return (
    <EqualisationPage
      actionBar={
        <EqualisationFooter
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          handleSaveAsDraft={handleSaveAsDraft}
          disabled={!allocationBreakdownIsValid}
          loading={updateEqualisation.isPending}
        />
      }
      subheading={match(currentSection)
        .with(
          EqualisationSection.Investments,
          () => 'Review equalised investments.',
        )
        .with(EqualisationSection.Expenses, () => 'Review expenses.')
        .with(EqualisationSection.Income, () => 'Review income.')
        .otherwise(() => 'Review equalised actions.')}
    >
      <Table
        isExpandableRow={() => true}
        isExpandedRow={({ id }) => expandedRows.has(id)}
        onRowExpand={({ id }) => {
          toggleRowExpansion(id);
        }}
        onRowClick={({ id }) => {
          toggleRowExpansion(id);
        }}
        getRowBackgroundColor={(row) => {
          const excludedInvestors = row.newAllocations.filter(
            (allocation) => allocation.excludeFromAllocation,
          );
          return match({
            allocationRule: row.allocationRule,
            hasExcludedInvestors: excludedInvestors.length > 0,
          })
            .with({ allocationRule: AllocationRule.Manual }, () => amber[50])
            .with({ hasExcludedInvestors: true }, () => grey[50])
            .otherwise(() => undefined);
        }}
        getExpandedContent={(action) => (
          <ActionCard
            key={action.id}
            actionIndex={actions.findIndex((a) => a.id === action.id)}
            action={action}
            baseCurrency={baseCurrency}
          />
        )}
        columns={columns}
        rows={actions}
      />
    </EqualisationPage>
  );
};

export default EqualisationContent;
